"use client";

import { FolderKanban } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import {
  PROJECT_STAGE_LABELS,
  PROJECT_STAGES,
  type ProjectRecord,
  type ProjectStage,
  WATCH_STATE_LABELS,
  type WatchState,
} from "@/lib/aicheck/history/types";
import { projectDetailPath } from "@/lib/aicheck/routes";
import { authService } from "@/services/auth";
import { type SaveState, SaveStatusIndicator } from "./SaveStatusIndicator";

const WATCH_STATES: WatchState[] = ["active", "paused", "closed"];

async function authedFetch(url: string, init?: RequestInit) {
  const token = await authService.getIdToken();
  if (!token) throw new Error("未認証です。再度サインインしてください。");
  return fetch(apiUrl(url), {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

/**
 * ia-ui Step1: メイン画面の結果直下に出す案件管理バー。
 * ステージ・出発日・ウォッチをその場で編集でき、ワークスペースへの往復を不要にする。
 * サーバ履歴に案件が未登録（未実行）の間は何も表示しない。
 */
export function ProjectManagementBar({
  projectParam,
  refreshKey,
}: {
  projectParam: string;
  refreshKey?: string | number;
}) {
  const [project, setProject] = useState<ProjectRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>({ status: "idle" });
  const lastPatchRef = useRef<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    if (!projectParam) {
      setProject(null);
      return;
    }
    try {
      const res = await authedFetch(
        `/api/aicheck/projects/${encodeURIComponent(projectParam)}`,
      );
      if (!res.ok) {
        setProject(null);
        return;
      }
      const data = (await res.json()) as { project: ProjectRecord };
      setProject(data.project);
    } catch {
      setProject(null);
    }
  }, [projectParam]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey intentionally triggers refetch
  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const patchProject = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!project) return;
      lastPatchRef.current = patch;
      setSaving(true);
      setSaveState({ status: "saving" });
      try {
        const res = await authedFetch(
          `/api/aicheck/projects/${encodeURIComponent(project.projectKey)}`,
          { method: "PATCH", body: JSON.stringify(patch) },
        );
        if (res.ok) {
          const data = (await res.json()) as { project: ProjectRecord };
          setProject(data.project);
          setSaveState({ status: "saved", at: Date.now() });
        } else {
          setSaveState({ status: "error" });
        }
      } catch {
        setSaveState({ status: "error" });
      } finally {
        setSaving(false);
      }
    },
    [project],
  );

  const retryPatch = useCallback(() => {
    if (lastPatchRef.current) void patchProject(lastPatchRef.current);
  }, [patchProject]);

  if (!project) return null;

  return (
    <div className="mb-6 flex flex-wrap items-end gap-4 rounded-lg border border-zinc-200 bg-white p-3 text-sm shadow-sm">
      <span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500">
        <FolderKanban size={14} />
        案件管理
        {project.sourceKind === "external" && (
          <span
            className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700"
            title={project.sourceLabel ?? "外部データ"}
          >
            外部データ
          </span>
        )}
      </span>
      <label className="flex flex-col gap-1 text-xs text-zinc-500">
        ステージ
        <select
          value={project.stage}
          disabled={saving}
          onChange={(e) =>
            void patchProject({ stage: e.target.value as ProjectStage })
          }
          className="h-8 rounded border border-zinc-300 bg-white px-2 text-sm text-zinc-800"
        >
          {PROJECT_STAGES.map((stage) => (
            <option key={stage} value={stage}>
              {PROJECT_STAGE_LABELS[stage]}
            </option>
          ))}
        </select>
      </label>
      <label
        className="flex flex-col gap-1 text-xs text-zinc-500"
        title="旅程データに出発日がある場合、チェック実行時に旅程の値へ自動更新されます。手動入力は旅程に出発日が無い場合の補完用です。"
      >
        出発日
        <input
          type="date"
          value={project.departureDate ?? ""}
          disabled={saving}
          onChange={(e) =>
            void patchProject({ departureDate: e.target.value || null })
          }
          className="h-8 rounded border border-zinc-300 bg-white px-2 text-sm text-zinc-800"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-zinc-500">
        ウォッチ状態
        <select
          value={project.watchState}
          disabled={saving}
          onChange={(e) =>
            void patchProject({ watchState: e.target.value as WatchState })
          }
          className="h-8 rounded border border-zinc-300 bg-white px-2 text-sm text-zinc-800"
        >
          {WATCH_STATES.map((state) => (
            <option key={state} value={state}>
              {WATCH_STATE_LABELS[state]}
            </option>
          ))}
        </select>
      </label>
      <div className="ml-auto flex items-center gap-4">
        <SaveStatusIndicator state={saveState} onRetry={retryPatch} />
        <a
          href={projectDetailPath(project.projectKey)}
          className="text-xs font-medium text-indigo-600 hover:underline"
          title="実績メモ・類似事例・提案アクションの詳細"
        >
          実績メモ・類似事例 →
        </a>
      </div>
    </div>
  );
}
